import { FC, KeyboardEvent, MouseEvent, ReactNode, memo, useMemo } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import { RgBanner as RgBannerData, RgBannerVariant } from '@app/graphql'
import { useRgBanner } from '@app/hooks/useRgBanner'

interface RgBannerViewProps {
  banner: RgBannerData
  messages: string[]
  className?: string
}

const isTouchDevice = (): boolean => window.matchMedia?.('(pointer: coarse)').matches ?? false

const telHref = (phone: string): string => `tel:${phone}`

/** Help link first, falling back to the helpline when a market only has a phone number (e.g. PE). */
const clickThroughUrl = (banner: RgBannerData): string | null =>
  banner.helpUrl || (banner.helpPhone ? telHref(banner.helpPhone) : null)

const openUrl = (url: string): void => {
  if (url.startsWith('tel:')) {
    window.location.href = url
    return
  }
  window.open(url, '_blank', 'noopener,noreferrer')
}

/**
 * The whole banner is the help click-through; inner links (tel, "Learn more") stop propagation so
 * they open their own target. A div with role="link" avoids nesting anchors.
 */
const ClickableBanner: FC<{ url: string | null; label: string; children: ReactNode; className?: string }> = ({
  url,
  label,
  children,
  className,
}) => {
  if (!url) return <div className={className}>{children}</div>

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      openUrl(url)
    }
  }

  return (
    <div
      className={className}
      role="link"
      tabIndex={0}
      aria-label={label}
      onClick={() => openUrl(url)}
      onKeyDown={onKeyDown}
      style={{ cursor: 'pointer' }}
    >
      {children}
    </div>
  )
}

const stop = (e: MouseEvent) => e.stopPropagation()

const LearnMore: FC<{ url?: string | null }> = ({ url }) => {
  const { t } = useTranslation()
  if (!url) return null
  return (
    <StyledLearnMore href={url} target="_blank" rel="noopener noreferrer" onClick={stop}>
      {t('rgBanner.learnMore')}
    </StyledLearnMore>
  )
}

const StandardBanner: FC<RgBannerViewProps> = ({ banner, messages, className }) => {
  const { t } = useTranslation()
  const lines = messages.length ? messages : [ t('rgBanner.betResponsibly') ]
  const phoneAsLink = useMemo(isTouchDevice, [])

  const phone = banner.helpPhoneLabel && (
    banner.helpPhone && phoneAsLink
      ? <a href={telHref(banner.helpPhone)} onClick={stop}>{banner.helpPhoneLabel}</a>
      : <span>{banner.helpPhoneLabel}</span>
  )

  return (
    <StyledWrapper className={className}>
      <StyledStandard url={clickThroughUrl(banner)} label={lines.join('. ')}>
        <StyledAgeBadge>{banner.ageLabel}</StyledAgeBadge>
        <StyledText>
          {lines.map(line => <StyledLine key={line}>{line}</StyledLine>)}
          {(banner.footer || phone) && (
            <StyledFooter>
              {banner.footer}
              {banner.footer && phone && ' · '}
              {phone}
            </StyledFooter>
          )}
        </StyledText>
      </StyledStandard>
      <LearnMore url={banner.regulatorUrl} />
    </StyledWrapper>
  )
}

/** Spanish DGOJ format (BOE Annex II) — fixed styling that deliberately ignores the tenant theme. */
const DgojBanner: FC<RgBannerViewProps> = ({ banner, messages, className }) => {
  const message = messages[0] ?? banner.shortMessage ?? ''

  // Spain exception: when an image is configured (18+ / juego seguro / juego autorizado), show only that.
  if (banner.imageUrl) {
    return (
      <StyledWrapper className={className}>
        <StyledDgojImage url={clickThroughUrl(banner)} label={`${banner.ageLabel} juego seguro, juego autorizado`}>
          <img src={banner.imageUrl} alt={`${banner.ageLabel} juego seguro, juego autorizado`} />
        </StyledDgojImage>
        <LearnMore url={banner.regulatorUrl} />
      </StyledWrapper>
    )
  }

  return (
    <StyledWrapper className={className}>
      <StyledDgojFrame url={clickThroughUrl(banner)} label={`${message}. ${banner.footer ?? ''}`}>
        <StyledDgojMessage>
          <span className="full">{message}</span>
          {banner.shortMessage && <span className="short">{banner.shortMessage}</span>}
        </StyledDgojMessage>
        {banner.footer && <StyledDgojMinors>{banner.footer}</StyledDgojMinors>}
      </StyledDgojFrame>
      <LearnMore url={banner.regulatorUrl} />
    </StyledWrapper>
  )
}

const ImageBanner: FC<RgBannerViewProps> = ({ banner, messages, className }) => (
  <StyledWrapper className={className}>
    <StyledImage
      url={clickThroughUrl(banner)}
      label={`${banner.ageLabel} ${messages.join('. ')}`}
    >
      <img src={banner.imageUrl ?? ''} alt={`${banner.ageLabel} ${messages.join('. ')}`} />
    </StyledImage>
    <LearnMore url={banner.regulatorUrl} />
  </StyledWrapper>
)

export const RgBannerView: FC<RgBannerViewProps> = props => {
  switch (props.banner.variant) {
    case RgBannerVariant.Dgoj:
      return <DgojBanner {...props} />
    case RgBannerVariant.Image:
      return props.banner.imageUrl ? <ImageBanner {...props} /> : <StandardBanner {...props} />
    default:
      return <StandardBanner {...props} />
  }
}

/** Legally required responsible-gambling banner for the user's country. */
const RgBanner: FC<{ className?: string }> = ({ className }) => {
  const { banner, messages, loading } = useRgBanner()
  if (loading || !banner) return null
  return <RgBannerView banner={banner} messages={messages} className={className} />
}

const StyledWrapper = styled.div`
  margin: 0 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const StyledLearnMore = styled.a`
  align-self: flex-end;
  font-size: var(--font-size-small);
  color: var(--text-quaternary);
  text-decoration: underline;
`

const StyledStandard = styled(ClickableBanner)`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 16px;
  background: var(--bg-primary);
  color: var(--text-primary);
`

const StyledAgeBadge = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 2px solid currentColor;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
`

const StyledText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const StyledLine = styled.span`
  font-size: var(--font-size-normal);
  font-weight: 500;
  line-height: 1.3;
`

const StyledFooter = styled.span`
  margin-top: 2px;
  font-size: var(--font-size-small);
  color: var(--text-quaternary);

  a {
    color: inherit;
    text-decoration: underline;
  }
`

const StyledDgojFrame = styled(ClickableBanner)`
  container-type: inline-size;
  background: #fff;
  color: #000;
  border: 18px solid #000;
  box-shadow: 0 0 0 2px #fff;
  padding: 10px 12px;
  text-align: center;
  font-family: Helvetica, Arial, sans-serif;
  font-weight: 700;
  text-transform: lowercase;
  line-height: 1.2;
`

const StyledDgojMessage = styled.div`
  font-size: 16px;

  .short {
    display: none;
  }

  @container (max-width: 200px) {
    .full {
      display: none;
    }
    .short {
      display: inline;
    }
  }
`

const StyledDgojMinors = styled.div`
  margin-top: 4px;
  font-size: 9.6px;
`

const StyledDgojImage = styled(ClickableBanner)`
  align-self: center;
  width: 100%;
  max-width: 300px;

  img {
    display: block;
    width: 100%;
    height: auto;
  }
`

const StyledImage = styled(ClickableBanner)`
  border-radius: 16px;
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    height: 80px;
    object-fit: cover;
  }
`

export default memo(RgBanner)
